import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { fetchTownhallAdmin, saveTownhallAdmin, resolveTownhallImageSrc } from "@/lib/townhall/townhall-service";
import { TownhallImageUpload } from "@/components/townhall/TownhallImageUpload";
import { normalizeSlokaSlide, prepareSlidesForSave } from "@/lib/chanting/sloka-transliteration";

function emptySlide(timestamp = 0) {
  return {
    timestamp,
    sanskrit: "",
    transliteration_en: "",
    transliteration_te: "",
    transliteration_hi: "",
  };
}

function slidesToEditorRows(slides) {
  return (slides || []).map((s) => {
    const n = normalizeSlokaSlide(s);
    return {
      timestamp: n.timestamp,
      sanskrit: n.sanskrit,
      transliteration_en: n.transliteration.en || "",
      transliteration_te: n.transliteration.te || "",
      transliteration_hi: n.transliteration.hi || "",
    };
  });
}

/**
 * Admin panel — deity image, description, Sanskrit + multi-language transliteration (no meanings).
 */
export function TownhallAdminEditor() {
  const { chantId } = useParams();
  const navigate = useNavigate();
  const { canEditLibrary, isAdmin, isStaffRole } = useSacredAdmin();
  const canEdit = canEditLibrary || isAdmin || isStaffRole;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState("");
  const [deityImage, setDeityImage] = useState("");
  const [description, setDescription] = useState("");
  const [slides, setSlides] = useState([]);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTownhallAdmin(chantId);
      setTitle(data.detail?.title ?? chantId);
      setDeityImage(data.detail?.deity_image ?? data.override?.deity_image ?? "");
      setDescription(data.override?.description ?? data.detail?.description ?? "");
      const raw = data.override?.slides?.length ? data.override.slides : data.detail?.slides ?? [];
      setSlides(slidesToEditorRows(raw));
    } catch (e) {
      setError(null);
      setTitle(chantId?.replace(/_/g, " ") ?? "Chant");
      setDeityImage(`/townhall/asset/${chantId}`);
      setDescription("");
      setSlides([]);
      console.warn("Townhall admin load failed:", e.message);
    } finally {
      setLoading(false);
    }
  }, [chantId]);

  useEffect(() => {
    if (!canEdit) return;
    load();
  }, [canEdit, load]);

  const updateSlide = (idx, field, value) => {
    setSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const addSlide = () => {
    const lastTs = slides.length ? slides[slides.length - 1].timestamp : 0;
    setSlides((prev) => [...prev, emptySlide(lastTs + 5)]);
  };

  const removeSlide = (idx) => {
    setSlides((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await saveTownhallAdmin(chantId, {
        deity_image: deityImage.trim() || undefined,
        description: description.trim() || undefined,
        slides: prepareSlidesForSave(slides),
      });
      setMessage("Saved to D1. Sanskrit + transliteration will appear in Townhall and Prayer Hall chanting.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="min-h-screen py-24 px-4 text-center">
        <p className="text-muted-foreground">Admin or editor access required to edit Townhall content.</p>
        <Button asChild variant="link" className="mt-4">
          <Link to={`/townhall/${chantId}`}>Back to chant</Link>
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="townhall-admin-editor">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to={`/townhall/${chantId}`}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Townhall
          </Link>
        </Button>

        <h1 className="font-heading text-2xl mb-1">Edit: {title}</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Sanskrit original + transliteration in each language (same mantra sound, different script). Meanings are not
          shown during chanting.
        </p>

        {error && <p className="text-destructive text-sm mb-4">{error}</p>}
        {message && <p className="text-green-700 text-sm mb-4">{message}</p>}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Deity picture</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TownhallImageUpload
              chantId={chantId}
              imageUrl={deityImage}
              onUploaded={(url) => {
                setDeityImage(url);
                setMessage("Picture updated.");
              }}
            />
            <div>
              <label className="text-xs text-muted-foreground">Or paste image URL / path</label>
              <Input
                value={deityImage}
                onChange={(e) => setDeityImage(e.target.value)}
                placeholder="/images/sacred-library/deities/hanuman.svg"
              />
              {deityImage && (
                <p className="text-[10px] text-muted-foreground mt-1 truncate">
                  Resolved: {resolveTownhallImageSrc(deityImage)}
                </p>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Description</label>
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description for sādhakas..."
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Slokas — Sanskrit + transliteration</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Transliteration preserves the original sound. Do not enter meanings/translations here.
              </p>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addSlide}>
              <Plus className="w-4 h-4 mr-1" /> Add sloka
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {slides.map((slide, idx) => (
              <div key={idx} className="rounded border p-3 space-y-2 bg-muted/20" data-testid={`sloka-editor-${idx}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium">Sloka {idx + 1}</span>
                  <Button type="button" size="icon" variant="ghost" onClick={() => removeSlide(idx)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <Input
                  type="number"
                  step="0.1"
                  value={slide.timestamp ?? 0}
                  onChange={(e) => updateSlide(idx, "timestamp", Number(e.target.value))}
                  placeholder="Audio timestamp (seconds)"
                />
                <Textarea
                  rows={2}
                  value={slide.sanskrit ?? ""}
                  onChange={(e) => updateSlide(idx, "sanskrit", e.target.value)}
                  placeholder="Sanskrit (Devanagari) — original"
                  className="font-sanskrit"
                />
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Transliteration (EN / IAST)</label>
                <Input
                  value={slide.transliteration_en ?? ""}
                  onChange={(e) => updateSlide(idx, "transliteration_en", e.target.value)}
                  placeholder="e.g. śrīguru charan saroj raj"
                />
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Transliteration (TE)</label>
                <Input
                  value={slide.transliteration_te ?? ""}
                  onChange={(e) => updateSlide(idx, "transliteration_te", e.target.value)}
                  placeholder="Same sound in Telugu script / phonetic"
                />
                <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Transliteration (HI)</label>
                <Input
                  value={slide.transliteration_hi ?? ""}
                  onChange={(e) => updateSlide(idx, "transliteration_hi", e.target.value)}
                  placeholder="Same sound for Hindi readers"
                />
              </div>
            ))}
            {!slides.length && (
              <p className="text-sm text-muted-foreground">No slokas yet. Add Sanskrit + transliteration lines.</p>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save changes
          </Button>
          <Button variant="outline" onClick={() => navigate(`/townhall/${chantId}`)}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
